'use client';

import {
  ReactNode,
  useState,
  useEffect,
  createElement,
  useRef,
} from 'react';
import { createPortal } from 'react-dom';


export interface PortalInterface {
  children: ReactNode,
  wrapperId: string,
}


/**
 * @description Helper function to create an empty div with a given ID,
 * append it to the body, and return the element
 * @param wrapperId
 */
export const createAndAppendWrapper = (wrapperId: string): Element => {
  const wrapper = document.createElement('div');
  wrapper.setAttribute('id', wrapperId);
  document.body.appendChild(wrapper);
  return wrapper;
};


/**
 * @description Wrapper component that creates a Portal and renders content
 * in the provided container outside the default hierarchy
 * @param children
 * @param wrapperId
 */
export const Portal = ({ children, wrapperId = 'bj-portal' }: PortalInterface) => {
  const [wrapper, setWrapper] = useState<null | Element>(null);
  const isMountedRef = useRef(true);
  const elemCreatedRef = useRef(false);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;

    isMountedRef.current = true;

    let element = document.getElementById(wrapperId) as Element | null;

    // if  no element is found with wrapperId, create one and append it to body
    if (!element && document.body) {
      elemCreatedRef.current = true;
      element = createAndAppendWrapper(wrapperId);
    }

    if (isMountedRef.current && element) {
      setWrapper(element);
    }

    return () => {
      isMountedRef.current = false;

      if (typeof document === 'undefined') {
        return;
      }

      // Only cleanup if we created the element
      if (elemCreatedRef.current) {
        const elementToRemove = document.getElementById(wrapperId);
        if (elementToRemove && elementToRemove.parentNode) {
          try {
            elementToRemove.parentNode.removeChild(elementToRemove);
          } catch (error) {
            // Silently handle case where element was already removed
            console.warn('Portal cleanup: Element already removed', error);
          }
        }
      }
    };
  }, [wrapperId]);

  // wrapperElement state will be null on the very first render.
  if (wrapper === null) return null;

  // Double-check wrapper still exists in DOM before creating portal
  // This prevents insertBefore errors when wrapper is removed during render
  if (!document.body || !document.body.contains(wrapper)) {
    return null;
  }

  return createPortal(createElement('div', null, children), wrapper);
};
